import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, firstValueFrom, Observable, of } from 'rxjs';
import { API_CONFIG, ApiUrlBuilder } from '../../config/api.config';
import { AuthService } from '../auth.service';
import {
  Customer,
  CustomerCreateRequest,
  CustomerUpdateRequest,
  PaginatedCustomersResponse,
} from '../../models/customer.model';

/**
 * Serviço responsável pelas chamadas HTTP relacionadas ao gerenciamento de clientes.
 * Encapsula todas as requisições à API de clientes sem gerenciar estado local.
 */
@Injectable({
  providedIn: 'root',
})
export class CustomerApiService {
  readonly http = inject(HttpClient);
  readonly authService = inject(AuthService);
  private readonly transloco = inject(TranslocoService);

  /**
   * Busca clientes paginados na API.
   *
   * @param pageNumber Número da página (inicia em 1)
   * @param pageSize Quantidade de itens por página
   * @param searchTerm Termo de busca opcional para filtrar resultados
   * @returns Resposta paginada com a lista de clientes
   */
  async getCustomers(pageNumber = 1, pageSize = 10, searchTerm = ''): Promise<PaginatedCustomersResponse> {
    let params = new HttpParams().set('pageNumber', pageNumber.toString()).set('pageSize', pageSize.toString());

    if (searchTerm.trim()) {
      params = params.set('searchTerm', searchTerm.trim());
    }

    const response = await firstValueFrom(
      this.http
        .get<PaginatedCustomersResponse>(ApiUrlBuilder.client('getAll'), {
          headers: this.authService.getAuthHeaders(),
          params,
        })
        .pipe(
          catchError((error) => {
            console.error('Erro ao listar clientes:', error);
            return of({ items: [], totalCount: 0, pageNumber, pageSize });
          }),
        ),
    );

    return response ?? { items: [], totalCount: 0, pageNumber, pageSize };
  }

  /**
   * Busca todos os clientes sem paginação para uso em filtros e seletores.
   *
   * @returns Lista completa de clientes disponíveis
   */
  async getAllForFilter(): Promise<Customer[]> {
    const response = await firstValueFrom(
      this.http.get<Customer[]>(ApiUrlBuilder.client('listAll'), { headers: this.authService.getAuthHeaders() }).pipe(
        catchError((error) => {
          console.error('Erro ao listar clientes para filtro:', error);
          return of([]);
        }),
      ),
    );

    return response ?? [];
  }

  /**
   * Busca um cliente específico pelo identificador.
   *
   * @param id Identificador único do cliente
   * @returns Dados do cliente encontrado ou null em caso de erro
   */
  async getCustomerById(id: string): Promise<Customer | null> {
    return firstValueFrom(
      this.http
        .get<Customer>(ApiUrlBuilder.client('getById', id), {
          headers: this.authService.getAuthHeaders(),
        })
        .pipe(
          catchError((error) => {
            console.error('Erro ao buscar cliente:', error);
            return of(null);
          }),
        ),
    );
  }

  /**
   * Cria um novo cliente na API.
   *
   * @param data Dados sanitizados do cliente a ser criado
   * @returns Dados do cliente criado retornados pela API
   * @throws HttpErrorResponse em caso de falha na criação
   */
  async createCustomer(data: CustomerCreateRequest): Promise<Customer> {
    return firstValueFrom(
      this.http.post<Customer>(ApiUrlBuilder.client('create'), data, {
        headers: this.authService.getAuthHeaders(),
      }),
    );
  }

  /**
   * Atualiza os dados de um cliente existente.
   *
   * @param id Identificador único do cliente
   * @param data Dados atualizados do cliente
   * @throws HttpErrorResponse em caso de falha na atualização
   */
  async updateCustomer(id: string, data: CustomerUpdateRequest): Promise<void> {
    await firstValueFrom(
      this.http.put(`${ApiUrlBuilder.client('update', id)}${API_CONFIG.endpoints.client.updateSuffix}`, data, {
        headers: this.authService.getAuthHeaders(),
      }),
    );
  }

  /**
   * Atualiza o status de ativação de um cliente.
   *
   * @param id Identificador único do cliente
   * @param isActive Novo status de ativação
   * @throws HttpErrorResponse em caso de falha na atualização
   */
  async updateCustomerStatus(id: string, isActive: boolean): Promise<void> {
    await firstValueFrom(
      this.http
        .patch(`${ApiUrlBuilder.client('updateStatus', id)}/status`, isActive, {
          headers: this.authService.getAuthHeaders(),
        })
        .pipe(
          catchError((error) => {
            console.error('Erro ao atualizar status do cliente:', error);
            throw error;
          }),
        ),
    );
  }

  /**
   * Remove um cliente da base de dados.
   *
   * @param id Identificador único do cliente
   * @throws HttpErrorResponse em caso de falha na exclusão
   */
  async deleteCustomer(id: string): Promise<void> {
    await firstValueFrom(
      this.http.delete(ApiUrlBuilder.client('delete', id), { headers: this.authService.getAuthHeaders() }).pipe(
        catchError((error: HttpErrorResponse) => {
          console.error('Erro ao deletar cliente:', error);
          throw error;
        }),
      ),
    );
  }

  /**
   * Busca clientes filtrando pelo termo informado, retornando um Observable para uso com RxJS.
   *
   * @param term Texto a ser usado como filtro de busca
   * @returns Observable com a resposta paginada de clientes filtrados
   */
  searchCustomers(term: string): Observable<PaginatedCustomersResponse> {
    const params = new HttpParams().set('searchTerm', term);

    return this.http
      .get<PaginatedCustomersResponse>(ApiUrlBuilder.client('getAll'), {
        headers: this.authService.getAuthHeaders(),
        params,
      })
      .pipe(catchError(() => of({ items: [], totalCount: 0, pageNumber: 1, pageSize: 10 })));
  }

  /**
   * Converte erros HTTP em mensagens amigáveis para exibição na UI.
   *
   * @param error Erro HTTP retornado pela API
   * @returns Mensagem padronizada e legível para o usuário
   */
  getErrorMessage(error: HttpErrorResponse): string {
    if (
      error?.status === 404 &&
      (error.error === 'Referral.CodeNotFound' || error.error?.code === 'Referral.CodeNotFound')
    ) {
      return this.transloco.translate('referral.codeNotFound');
    }

    if (error?.error) {
      if (typeof error.error === 'string') return error.error;
      if (error.error?.message) return error.error.message;
      if (error.error?.title) return error.error.title;
    }
    switch (error?.status) {
      case 400:
        return this.transloco.translate('shared.apiErrors.invalidDataDetails');
      case 409:
        return typeof error.error === 'string'
          ? error.error
          : this.transloco.translate('shared.apiErrors.cnpjAlreadyExists');
      case 500:
        return this.transloco.translate('shared.apiErrors.internalServerRetry');
      default:
        return this.transloco.translate('shared.apiErrors.connectionServer');
    }
  }
}
